import React, {useCallback, useEffect, useState} from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import CachedIcon from "@material-ui/icons/Cached";
import RefreshIcon from "@material-ui/icons/Refresh";
import SaveIcon from "@material-ui/icons/Save";

import API from "../../../network/dashboardAPI";
import useAlert from "../../../services/useAlert";

const CONFIG_DETAILS = {
	data_limit: {
		label: "Search result limit",
		description: "Maximum number of products loaded for a search.",
		step: 1,
	},
	product_name: {
		label: "Product name",
		description: "Ranking weight for matches in the product name.",
	},
	brand_name: {
		label: "Brand name",
		description: "Ranking weight for matches in the brand name.",
	},
	category_name: {
		label: "Category name",
		description: "Ranking weight for matches in the category name.",
	},
	subcategory_name: {
		label: "Subcategory name",
		description: "Ranking weight for matches in the subcategory name.",
	},
	description: {
		label: "Product description",
		description: "Ranking weight for matches in the product description.",
	},
	how_to_use: {
		label: "How to use",
		description: "Ranking weight for matches in usage instructions.",
	},
	ingredients: {
		label: "Ingredients",
		description: "Ranking weight for matches in ingredients.",
	},
	discount: {
		label: "Discounted product",
		description: "Ranking boost applied to discounted products.",
	},
	priority: {
		label: "Priority product",
		description: "Ranking boost applied to priority products.",
	},
	new: {
		label: "New product",
		description: "Ranking boost applied to new arrivals.",
	},
	trending: {
		label: "Trending product",
		description: "Ranking boost applied to trending products.",
	},
};

const useStyles = makeStyles((theme) => ({
	root: {
		minHeight: "100vh",
		padding: theme.spacing(5, 3),
		backgroundColor: "#f7f7f7",
		[theme.breakpoints.down("sm")]: {
			padding: theme.spacing(9, 2, 4),
		},
	},
	header: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			flexDirection: "column",
		},
	},
	actions: {
		display: "flex",
		gap: theme.spacing(1),
		flexWrap: "wrap",
	},
	paper: {
		borderRadius: 12,
		overflow: "hidden",
		boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
	},
	intro: {
		padding: theme.spacing(2.5, 3),
		borderBottom: "1px solid #e8e8e8",
		backgroundColor: "#fff",
	},
	tableHead: {
		backgroundColor: "#f4f6f8",
	},
	nameCell: {
		minWidth: 280,
	},
	keyword: {
		display: "inline-block",
		marginTop: theme.spacing(0.5),
		padding: "2px 7px",
		borderRadius: 4,
		backgroundColor: "#f1f3f5",
		fontFamily: "monospace",
		fontSize: 12,
		color: "#5f6368",
	},
	valueCell: {
		width: 180,
	},
	valueInput: {
		width: 145,
	},
	actionCell: {
		width: 150,
	},
	loading: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1.5),
		padding: theme.spacing(6),
	},
	error: {
		padding: theme.spacing(2, 3),
		color: theme.palette.error.main,
		backgroundColor: "#fff4f4",
	},
}));

const getConfigDetails = (keyword) => CONFIG_DETAILS[keyword] || {
	label: keyword.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase()),
	description: "Search ranking configuration value.",
};

const validateValue = (config, rawValue) => {
	if (rawValue === "" || rawValue === null || rawValue === undefined) {
		return "Value is required.";
	}

	const value = Number(rawValue);
	if (!Number.isFinite(value)) return "Enter a valid number.";
	if (value < 0) return "Value cannot be negative.";
	if (value > 99999999.99) return "Value is too large.";
	if (config.keyword === "data_limit" && (!Number.isInteger(value) || value < 1)) {
		return "Data limit must be a whole number greater than zero.";
	}

	return null;
};

export default function SearchConfiguration() {
	const classes = useStyles();
	const alert = useAlert();
	const [configurations, setConfigurations] = useState([]);
	const [draftValues, setDraftValues] = useState({});
	const [fieldErrors, setFieldErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [updatingId, setUpdatingId] = useState(null);
	const [clearingCache, setClearingCache] = useState(false);
	const [loadError, setLoadError] = useState("");

	const fetchConfigurations = useCallback(async () => {
		setLoading(true);
		setLoadError("");

		try {
			const response = await API.getSearchConfigurations();
			const result = await response.json();

			if (!response.ok || !result.success) {
				throw new Error(result.message || result.error || "Unable to load search configuration.");
			}

			const list = Array.isArray(result.list) ? result.list : [];
			setConfigurations(list);
			setDraftValues(list.reduce((values, config) => ({
				...values,
				[config.id]: String(config.value),
			}), {}));
			setFieldErrors({});
		} catch (error) {
			setLoadError(error.message || "Unable to load search configuration.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchConfigurations();
	}, [fetchConfigurations]);

	const handleValueChange = (config, value) => {
		setDraftValues((current) => ({...current, [config.id]: value}));
		if (fieldErrors[config.id]) {
			setFieldErrors((current) => ({...current, [config.id]: ""}));
		}
	};

	const handleSave = async (config) => {
		const value = draftValues[config.id];
		const validationError = validateValue(config, value);

		if (validationError) {
			setFieldErrors((current) => ({...current, [config.id]: validationError}));
			return;
		}

		setUpdatingId(config.id);
		try {
			const numericValue = Number(value);
			const response = await API.updateSearchConfiguration({id: config.id, value: numericValue});
			const result = await response.json();

			if (!response.ok || !result.success) {
				throw new Error(result.message || result.error || "Unable to update configuration.");
			}

			setConfigurations((current) => current.map((item) => (
				item.id === config.id ? {...item, value: numericValue} : item
			)));
			setDraftValues((current) => ({...current, [config.id]: String(numericValue)}));
			alert.toast(`${getConfigDetails(config.keyword).label} updated`);
		} catch (error) {
			alert.error("Update failed", error.message || "Unable to update configuration.");
		} finally {
			setUpdatingId(null);
		}
	};

	const handleClearCache = async () => {
		const confirmation = await alert.confirm(
			"Clear search cache?",
			"Cached recent-search results will be removed and rebuilt as customers search again.",
			"Clear cache"
		);

		if (!confirmation.isConfirmed) return;

		setClearingCache(true);
		try {
			const response = await API.clearSearchConfigurationCache();
			const result = await response.json();

			if (!response.ok || !result.success) {
				throw new Error(result.message || result.error || "Unable to clear search cache.");
			}

			alert.success("Cache cleared", result.message || "Search cache cleared successfully.");
		} catch (error) {
			alert.error("Clear cache failed", error.message || "Unable to clear search cache.");
		} finally {
			setClearingCache(false);
		}
	};

	return (
		<div className={classes.root}>
			<div className={classes.header}>
				<div>
					<Typography variant="h5" gutterBottom>Search Configuration</Typography>
					<Typography variant="body2" color="textSecondary">
						Control how product search results are loaded and ranked.
					</Typography>
				</div>
				<div className={classes.actions}>
					<Button
						variant="outlined"
						startIcon={<RefreshIcon/>}
						onClick={fetchConfigurations}
						disabled={loading || updatingId !== null || clearingCache}
					>
						Refresh
					</Button>
					<Button
						variant="contained"
						color="secondary"
						startIcon={clearingCache ? <CircularProgress size={18} color="inherit"/> : <CachedIcon/>}
						onClick={handleClearCache}
						disabled={clearingCache || loading || updatingId !== null}
					>
						{clearingCache ? "Clearing..." : "Clear search cache"}
					</Button>
				</div>
			</div>

			<Paper className={classes.paper}>
				<div className={classes.intro}>
					<Typography variant="subtitle1">Ranking weights</Typography>
					<Typography variant="body2" color="textSecondary">
						Higher weights give matching products more influence in search-result ordering.
					</Typography>
				</div>

				{loadError && <Typography className={classes.error}>{loadError}</Typography>}

				{loading ? (
					<Box className={classes.loading}>
						<CircularProgress size={28}/>
						<Typography color="textSecondary">Loading configuration...</Typography>
					</Box>
				) : (
					<TableContainer>
						<Table aria-label="Search configuration">
							<TableHead className={classes.tableHead}>
								<TableRow>
									<TableCell>Configuration</TableCell>
									<TableCell>Value</TableCell>
									<TableCell align="right">Action</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{configurations.length === 0 ? (
									<TableRow>
										<TableCell colSpan={3} align="center">
											<Box py={4}>
												<Typography color="textSecondary">No search configuration found.</Typography>
											</Box>
										</TableCell>
									</TableRow>
								) : configurations.map((config) => {
									const details = getConfigDetails(config.keyword);
									const changed = Number(draftValues[config.id]) !== Number(config.value);
									const saving = updatingId === config.id;

									return (
										<TableRow key={config.id} hover>
											<TableCell className={classes.nameCell}>
												<Box display="flex" alignItems="center">
													<Typography variant="subtitle2">{details.label}</Typography>
													{changed && <Chip label="Unsaved" size="small" color="primary" style={{marginLeft: 8}}/>}
												</Box>
												<Typography variant="body2" color="textSecondary">{details.description}</Typography>
												<span className={classes.keyword}>{config.keyword}</span>
											</TableCell>
											<TableCell className={classes.valueCell}>
												<TextField
													className={classes.valueInput}
													variant="outlined"
													size="small"
													type="number"
													value={draftValues[config.id] === undefined ? "" : draftValues[config.id]}
													onChange={(event) => handleValueChange(config, event.target.value)}
													onKeyDown={(event) => {
														if (event.key === "Enter" && changed && !saving) handleSave(config);
													}}
													error={Boolean(fieldErrors[config.id])}
													helperText={fieldErrors[config.id] || " "}
													inputProps={{
														min: config.keyword === "data_limit" ? 1 : 0,
														step: details.step || 0.01,
														"aria-label": `${details.label} value`,
													}}
													disabled={saving || clearingCache}
												/>
											</TableCell>
											<TableCell align="right" className={classes.actionCell}>
												<Button
													variant="contained"
													color="primary"
													startIcon={saving ? <CircularProgress size={17} color="inherit"/> : <SaveIcon/>}
													onClick={() => handleSave(config)}
													disabled={!changed || saving || clearingCache || updatingId !== null}
												>
													{saving ? "Saving..." : "Save"}
												</Button>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Paper>
		</div>
	);
}
